import { C8Node } from "c8-platform";
import crews from "csi/access/crews";
import reflection from "csi/reflection";
import { RightPaneAllowedFunction } from "csi/state/rightPane";
import _ from "lodash";
import ProductType from "src/Apparel/ProductType";

/**
 * Return the target subtype setting attribute for the given base product
 * @param productNode
 */
function getSubtypeSetting(productNode: C8Node, attributeName: string): string[] {
	const productNodeType = productNode?.$Type;

	switch (productNodeType) {
		case "Food":
		case "FormulatedMaterial":
			return reflection.getSubtypeSetting(productNode?.Subtype, attributeName);

		case "Style":
		case "Material":
			return ProductType.getTypeSetting(productNode?.ProductType, attributeName) as string[];

		default:
			return null;
	}
}

/**
 * If the given node is a FLDS or contains a FLDS, return the FLDS subtype URL
 * @param dataSheetNode
 */
function getFldsSubtypeUrl(dataSheetNode: C8Node): string {
	const type = dataSheetNode?.$Type;
	const subtypeUrl = dataSheetNode?.Subtype;

	switch (type) {
		case "RecipeDataSheet":
			return reflection.getSubtypeSetting(subtypeUrl, "FoodLabelSubtype");

		case "FoodBOM":
			return reflection.getSubtypeSetting(subtypeUrl, "BOMLabelSubtype");

		case "FoodLabelDataSheet":
			return subtypeUrl;

		default:
			return null;
	}
}
/**
 * Check that the given node is a valid data sheet containing a FoodLabelDataSheet with the FoodIngredients tab
 * @param dataSheetNode
 */
function isValidDataSheet(dataSheetNode: C8Node): boolean {
	if (!dataSheetNode) {
		return false;
	}

	const fldsSubtypeUrl = getFldsSubtypeUrl(dataSheetNode);

	if (!fldsSubtypeUrl) {
		return false;
	}

	const dataSheetSection = reflection.getSubtypeSetting(fldsSubtypeUrl, "DataSheetSections");

	return !_.isEmpty(dataSheetSection) && dataSheetSection.includes("LabelDataSheetSections:FoodIngredient");
}

/**
 * Check if the current user has the correct access roles to view the Compliance Analysis right pane
 */
function hasAccessRoles(node: C8Node): boolean {
	const complianceRoles = ["Product Compliance Analysis Viewer", "Product Compliance Analysis Editor", "Product Compliance Analysis Administrator"];

	return crews.isInRoles(complianceRoles, node);
}

/**
 * Returns a function checking if the type exists in 'allowedTypes', subtypeSettings has Ingredient
 * in DataShetSections, and if the parentNode has non-empty ComplianceAnalysisTypes
 * @param allowedTypes - the list of allowed types
 */
export function isAllowedForBaseProductTypeFuncFLDS(allowedTypes: string[]): RightPaneAllowedFunction {
	return (type, _subtype, node, rootCache) => {
		// Check that the context node exists and is a valid type
		if (!node || !allowedTypes.includes(type)) {
			return false;
		}

		// Check that the current user has the required access roles
		if (!hasAccessRoles(node)) {
			return false;
		}

		// Check that the parent product has a ComplianceAnalysisType set 
		const parentUrl = node.__Parent__;
		const parentNode = rootCache.get(parentUrl);
		const complianceAnalysisTypes = getSubtypeSetting(parentNode, "ComplianceAnalysisTypes");

		if (_.isEmpty(complianceAnalysisTypes)) {
			return false;
		}

		// Check that the context node is a valid data sheet containing a FoodLabelDataSheet with the FoodIngredients tab
		return isValidDataSheet(node);
	};
}

/**
 * Returns a function checking if the type exists in 'allowedTypes', subtypeSettings has Ingredient
 * in DataShetSections, and if the parentNode has non-empty ComplianceAnalysisTypes
 * @param allowedTypes - the list of allowed types
 */
export function isAllowedForBaseProductTypeFuncBaseProduct(allowedTypes: string[]): RightPaneAllowedFunction {
	return (type, _subtype, node, rootCache) => {
		// Check that the context node exists and is a valid type
		if (!node || !allowedTypes.includes(type)) {
			return false;
		}

		// Check that the current user has the required access roles
		if (!hasAccessRoles(node)) {
			return false;
		}

		// Check that the base product has a ComplianceAnalysisType set 
		const complianceAnalysisTypes = getSubtypeSetting(node, "ComplianceAnalysisTypes");

		if (_.isEmpty(complianceAnalysisTypes)) {
			return false;
		}

		// Check that the base product contains at least one Food Label Data Sheet with FoodIngredients enabled
		const hasValidDataSheet = node.DataSheets.some((dataSheetUrl) => isValidDataSheet(rootCache.get(dataSheetUrl)));
		return hasValidDataSheet;
	};
}

/** check to see if user has Product Compliance Analysis Admin role */
export function hasComplianceAnalysisAdminRole(): boolean {
	return crews.isInGlobalRoles(["Product Compliance Analysis Administrator"]);
}